import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  FileCheck,
  FileText,
  Landmark,
  Loader2,
  Palette,
  RefreshCw,
  ShieldCheck,
  Store,
  UserCog,
  UserPlus,
} from "lucide-react";
import toast from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { DashboardAccountantHandoffBanner } from "@/pages/dashboard/DashboardAccountantHandoffBanner";
import { dashboardKeys, tenantKeys } from "@/lib/queryKeys";
import { completeOnboarding, getTenantCurrent } from "@/services/authService";
import { dismissDashboardAlert, getDashboard } from "@/services/dashboardService";
import {
  ONBOARDING_NUDGE_ACCOUNTANT_INVITE,
  ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED,
  ONBOARDING_NUDGE_BRAND,
  ONBOARDING_NUDGE_ACCT_CAE_LOADED,
  ONBOARDING_NUDGE_CAE,
  ONBOARDING_NUDGE_CFE_IMPORT,
  ONBOARDING_NUDGE_CFE_IMPORT_RECIBIDAS,
} from "@/hooks/useOnboardingPending";
import {
  getFiscalProfile,
  retryDgiOnboardingAutoFlow,
} from "@/services/fiscalService";
import {
  OnboardingBrandModal,
  OnboardingCertModal,
  OnboardingCfeModal,
  OnboardingInviteAccountantModal,
  OnboardingSucursalModal,
} from "@/pages/dashboard/OnboardingActionModals";
import { ImportRecibidasCfeDialog } from "@/pages/tenants/ImportRecibidasCfeDialog";
import { importRecibidasCfes } from "@/services/invoiceService";
import { AddCaeDialog } from "@/pages/settings/AddCaeDialog";
import { computeCaeAccountStep } from "@/pages/dashboard/needsCaeAccountStep";

type StepKey =
  | "portal_user"
  | "company_info"
  | "cae_download"
  | "proveedor_software";

interface StepDef {
  key: StepKey;
  labelKey: string;
  fallback: string;
}

const STEPS: ReadonlyArray<StepDef> = [
  { key: "portal_user", labelKey: "dashboard.onboardingCard.step.portalUser", fallback: "Usuario e-Factura" },
  { key: "company_info", labelKey: "dashboard.onboardingCard.step.companyInfo", fallback: "Datos de la empresa" },
  { key: "cae_download", labelKey: "dashboard.onboardingCard.step.caeDownload", fallback: "Descarga de CAEs" },
  { key: "proveedor_software", labelKey: "dashboard.onboardingCard.step.proveedorSoftware", fallback: "Configurar Fiscalo como tu proveedor de e-Factura" },
];

// Scratch tenants skip proveedor_software during onboarding (Fiscalo isn't
// yet homologated as their proveedor — that's a manual DGI registration
// they run after the homologation process). Drop the step from the
// dashboard's progress card for them so it doesn't sit "pending" forever.
const SCRATCH_STEPS: ReadonlyArray<StepDef> = STEPS.filter(
  (s) => s.key !== "proveedor_software",
);

function isStringRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

interface DashboardOnboardingCardProps {
  /**
   * Delegated-owner mode — the owner chose "my accountant continues" and paid,
   * so the technical steps (cert, sucursal, fiscal profile, DGI auto-flow) are
   * the accountant's job. In this mode we show the full checklist for context,
   * surface the accountant-handoff banner under the hero, mark the technical
   * steps read-only ("Lo hace tu contador"), and leave the owner-doable steps
   * (logo/brand, CFE import) actionable.
   */
  delegated?: boolean;
}

export function DashboardOnboardingCard({
  delegated = false,
}: DashboardOnboardingCardProps = {}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: tenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
    refetchInterval: (q): number | false => {
      const tn = q.state.data;
      if (!tn) return false;
      const progress = isStringRecord(tn.dgiAutoOnboardingProgress)
        ? tn.dgiAutoOnboardingProgress
        : null;
      if (!progress) return false;
      if (!progress.portal_user) return false;
      const stepsForTenant =
        tn.onboardingSituationChoice === "scratch" ? SCRATCH_STEPS : STEPS;
      const allDone = stepsForTenant.every((s) => Boolean(progress[s.key]));
      const failed = isStringRecord(progress._failed) ? progress._failed : null;
      const anyFailed = stepsForTenant.some(
        (s) => failed && Boolean(failed[s.key]),
      );
      return !allDone && !anyFailed ? 10_000 : false;
    },
  });

  const autoFlow = useMemo(() => {
    if (!tenant) return null;
    const progress = isStringRecord(tenant.dgiAutoOnboardingProgress)
      ? tenant.dgiAutoOnboardingProgress
      : null;
    if (!progress) return null;
    if (!progress.portal_user) return null;
    const stepsForTenant =
      tenant.onboardingSituationChoice === "scratch" ? SCRATCH_STEPS : STEPS;
    const allDone = stepsForTenant.every((s) => Boolean(progress[s.key]));
    if (allDone) return null;
    const failed = isStringRecord(progress._failed) ? progress._failed : {};
    const firstFailed =
      stepsForTenant.find((s) => Boolean(failed[s.key])) ?? null;
    const firstPending =
      stepsForTenant.find((s) => !progress[s.key]) ?? null;
    return {
      progress,
      failed: failed as Partial<Record<StepKey, string>>,
      firstFailed,
      firstPending,
      doneCount: stepsForTenant.filter((s) => Boolean(progress[s.key])).length,
      totalCount: stepsForTenant.length,
    };
  }, [tenant]);

  const navigate = useNavigate();

  // Fiscal-profile presence drives the agenda generator (IVA, BPS, IRAE,
  // IRPF, IRNR obligations are all keyed off the profile's `taxes` set).
  // Without it the fiscal-agenda + accountant filter chips have nothing
  // to show, so prompt the user to configure it from here. "Done" means
  // a profile exists with at least one tax tracked.
  const { data: fiscalProfile, isLoading: fiscalProfileLoading } = useQuery({
    queryKey: ["fiscal-profile"],
    queryFn: getFiscalProfile,
    enabled: Boolean(tenant?.id),
  });
  // Fiscal profile drives the agenda generator (IVA/BPS/IRAE/IRPF/IRNR) — a
  // paid-plan feature. Free-plan tenants don't get it, so don't nudge them to
  // configure it (and don't count it in the hero total).
  const fiscalProfilePending =
    Boolean(tenant) &&
    tenant?.planId !== "free" &&
    (!fiscalProfile || (fiscalProfile.taxes?.length ?? 0) === 0);

  // Demo / showcase tenants run with strict DGI bypass — invoices land
  // confirmed locally without ever calling DGI, so they have no use for
  // a cert. Drop the nudge entirely for them.
  const certPending =
    Boolean(tenant) && !tenant?.bypassDgi && !tenant?.hasDgiCredentials;
  // CFE-import / brand / accountant nudges are tenant-wide dismissals
  // now (company_dismissed_dashboard_alert table). The previous per-
  // browser localStorage flags meant a coworker on a different device
  // saw nudges that had already been dismissed elsewhere. useOnboarding
  // Pending reads the same dismissedAlertIds[] so the gate and the card
  // can't disagree.
  const tenantId = tenant?.id ?? null;
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: dashboardKeys.metrics(),
    queryFn: getDashboard,
    enabled: Boolean(tenantId),
  });
  const dismissedAlertIds = dashboardData?.dismissedAlertIds ?? [];
  // CFE import is two independent steps — emitted (what the company issued)
  // and received (from suppliers). Each has its own dismiss flag so the owner
  // can mark / import them separately.
  const cfeEmitidosPending =
    Boolean(tenant) &&
    !dismissedAlertIds.includes(ONBOARDING_NUDGE_CFE_IMPORT);
  const cfeRecibidasPending =
    Boolean(tenant) &&
    !dismissedAlertIds.includes(ONBOARDING_NUDGE_CFE_IMPORT_RECIBIDAS);
  const brandPending =
    Boolean(tenant) &&
    // Must test the EFFECTIVE invoice logo, not invoiceLogoImage alone. A null
    // invoiceLogoImage means "reuse the site logo" (that is how the settings
    // toggle stores sharing), so a tenant who has uploaded a logo and shares it
    // across both surfaces has null here — testing the raw column made this
    // nudge reappear and looked like onboarding had been reset.
    !(tenant?.invoiceLogoImage ?? tenant?.profileImage) &&
    !dismissedAlertIds.includes(ONBOARDING_NUDGE_BRAND);
  // Done when the tenant already has an accountant member (auto-detected via
  // the dashboard's hasAccountant signal) OR the nudge was manually dismissed.
  const accountantPending =
    Boolean(tenant) &&
    !dashboardData?.hasAccountant &&
    !dismissedAlertIds.includes(ONBOARDING_NUDGE_ACCOUNTANT_INVITE);
  // Confirm-sucursal nudge: fires only when GetEntidad didn't auto-detect the
  // sucursal at onboarding (createCompany leaves the mark unstamped on fallback;
  // existing tenants were grandfathered by BackfillSucursalConfirmedAlert). Skipped
  // for DGI-bypass tenants — they don't file reportes, so CodSuc is irrelevant.
  const sucursalPending =
    Boolean(tenant) &&
    !tenant?.bypassDgi &&
    !dismissedAlertIds.includes(ONBOARDING_NUDGE_ACCT_SUCURSAL_CONFIRMED);

  const dismissNudgeMutation = useMutation({
    mutationFn: (alertId: string) => dismissDashboardAlert(alertId),
    onSuccess: async () => {
      // Single source of truth — dashboard query refresh feeds both
      // this card and the useOnboardingPending gate.
      await queryClient.invalidateQueries({
        queryKey: dashboardKeys.metrics(),
      });
    },
  });
  const dismissCaeNudge = useCallback((): void => {
    dismissNudgeMutation.mutate(ONBOARDING_NUDGE_CAE);
    toast.success(
      t(
        "dashboard.onboardingCard.caeMarkedDone",
        "Listo — marcamos los CAE como completado.",
      ),
    );
  }, [dismissNudgeMutation, t]);
  const dismissCfeEmitidosNudge = useCallback((): void => {
    dismissNudgeMutation.mutate(ONBOARDING_NUDGE_CFE_IMPORT);
    toast.success(
      t(
        "dashboard.onboardingCard.cfeEmitidosMarkedDone",
        "Listo — marcamos los CFE emitidos como completado.",
      ),
    );
  }, [dismissNudgeMutation, t]);
  const dismissCfeRecibidasNudge = useCallback((): void => {
    dismissNudgeMutation.mutate(ONBOARDING_NUDGE_CFE_IMPORT_RECIBIDAS);
    toast.success(
      t(
        "dashboard.onboardingCard.cfeRecibidasMarkedDone",
        "Listo — marcamos los CFE recibidos como completado.",
      ),
    );
  }, [dismissNudgeMutation, t]);
  const dismissBrandNudge = useCallback((): void => {
    dismissNudgeMutation.mutate(ONBOARDING_NUDGE_BRAND);
    toast.success(
      t(
        "dashboard.onboardingCard.brandMarkedDone",
        "Listo — marcamos la personalización como completada.",
      ),
    );
  }, [dismissNudgeMutation, t]);
  const dismissAccountantNudge = useCallback((): void => {
    dismissNudgeMutation.mutate(ONBOARDING_NUDGE_ACCOUNTANT_INVITE);
    toast.success(
      t(
        "dashboard.onboardingCard.accountantMarkedDone",
        "Listo — marcamos la invitación al contador como completada.",
      ),
    );
  }, [dismissNudgeMutation, t]);

  const retryMutation = useMutation({
    mutationFn: retryDgiOnboardingAutoFlow,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
    },
    onSuccess: (res) => {
      if (res.accepted) {
        toast.success(t("dashboard.onboardingCard.retryStarted", "Reintentando configuración…"));
      } else {
        toast.error(res.message ?? t("dashboard.onboardingCard.retryFailed", "No pudimos reintentar."));
      }
    },
    onError: () => {
      toast.error(t("dashboard.onboardingCard.retryFailed", "No pudimos reintentar."));
    },
  });

  // Auto-recover from a stuck step. When a step has shown AHORA for more than
  // STUCK_AFTER_MS without the chain advancing, fire /retry — which directly
  // hits orchestrator.resumeAuto, bypassing BullMQ entirely. Necessary because
  // the cron path goes through a Redis-backed scheduled-tasks queue that can
  // dedup or stall in ways that aren't always observable from the frontend.
  // One-shot per dashboard mount via ref; the backend's pre-cancel + fresh
  // sessionId means a single trigger is enough.
  const STUCK_AFTER_MS = 90_000;
  const autoRetryFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoFlow) return;
    if (autoFlow.firstFailed) return; // user-actionable already; let them click
    const stepKeyAndTs = STEPS.map((s) => {
      const v = autoFlow.progress[s.key];
      return [s.key, typeof v === "string" ? Date.parse(v) : NaN] as const;
    }).filter(([, t]) => Number.isFinite(t));
    if (stepKeyAndTs.length === 0) return; // nothing stamped yet → user is mid-portal_user
    const mostRecentTs = Math.max(...stepKeyAndTs.map(([, t]) => t));
    const stuckSinceMs = Date.now() - mostRecentTs;
    if (stuckSinceMs < STUCK_AFTER_MS) return;
    if (!autoFlow.firstPending) return; // chain already complete
    const fingerprint = `${autoFlow.firstPending.key}:${mostRecentTs}`;
    if (autoRetryFiredRef.current === fingerprint) return;
    autoRetryFiredRef.current = fingerprint;
    if (!retryMutation.isPending) retryMutation.mutate();
  }, [autoFlow, retryMutation]);

  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [cfeModalOpen, setCfeModalOpen] = useState(false);
  const [recibidasModalOpen, setRecibidasModalOpen] = useState(false);
  const [accountantModalOpen, setAccountantModalOpen] = useState(false);
  const [sucursalModalOpen, setSucursalModalOpen] = useState(false);
  const [caeModalOpen, setCaeModalOpen] = useState(false);

  // Hide only for guest accountants (membership role 'accountant') — they're
  // not configuring the tenant. Accountants who actually own this tenant
  // (created it on a client's behalf) still need the checklist to finish
  // DGI setup.
  // CAE step (dismiss-based, ONBOARDING_NUDGE_CAE) pending — computed here (not
  // just in the render list below) so it can gate both the "nothing pending"
  // hide condition and the completion stamp.
  const caeGateProgress = isStringRecord(tenant?.dgiAutoOnboardingProgress)
    ? tenant?.dgiAutoOnboardingProgress
    : null;
  const caeGateAutoFlow = Boolean(caeGateProgress?.portal_user);
  // Same pure rule the render list below uses. It was duplicated inline here,
  // and THIS copy is the one gating onboardingChecklistComplete — so a fix
  // applied only to the rendered row would have left the onboardingCompletedAt
  // stamp broken for exactly the tenants it was meant to unblock.
  const caeGate = computeCaeAccountStep({
    bypassDgi: Boolean(tenant?.bypassDgi),
    autoFlowApplies: caeGateAutoFlow,
    dismissedSelf: dismissedAlertIds.includes(ONBOARDING_NUDGE_CAE),
    dismissedAccountant: dismissedAlertIds.includes(
      ONBOARDING_NUDGE_ACCT_CAE_LOADED,
    ),
  });
  const caePending = Boolean(tenant) && caeGate.applicable && !caeGate.done;

  // The dashboard checklist is the finish line for tenants who onboarded via
  // homologación/regularization + this card (they never hit the wizard's
  // completeOnboarding). When nothing is left pending, stamp onboardingCompletedAt
  // once — otherwise they stay onboardingCompletedAt=null → "En configuración"
  // forever despite being operational. Mirrors the "nothing pending" hide gate.
  const onboardingChecklistComplete =
    tenant != null &&
    tenant.onboardingCompletedAt == null &&
    !fiscalProfileLoading &&
    !dashboardLoading &&
    !autoFlow &&
    !certPending &&
    !cfeEmitidosPending &&
    !cfeRecibidasPending &&
    !fiscalProfilePending &&
    !brandPending &&
    !sucursalPending &&
    !caePending;
  const completeOnboardingFiredRef = useRef(false);
  useEffect(() => {
    if (!onboardingChecklistComplete || completeOnboardingFiredRef.current) {
      return;
    }
    // Fire once per mount. On failure keep the ref set (no retry loop — a page
    // reload retries); completeOnboarding is a no-op once the date is stamped.
    completeOnboardingFiredRef.current = true;
    void completeOnboarding()
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: tenantKeys.current(user?.tenantId ?? undefined),
        }),
      )
      .catch(() => {
        /* best-effort; leave the card as-is */
      });
  }, [onboardingChecklistComplete, queryClient, user?.tenantId]);

  if (user?.role === "accountant") return null;

  // Nothing until the queries that decide each nudge have actually settled.
  // Several "pending" flags are derived from absence — `!fiscalProfile`, an
  // empty `dismissedAlertIds` — so mid-flight they read as "step not done"
  // and the card rendered a full setup checklist for a beat before the data
  // arrived and it disappeared again. Rendering nothing is the honest state:
  // we don't yet know whether there is anything to prompt for.
  if (!tenant || fiscalProfileLoading || dashboardLoading) return null;

  // The card stays open while any real onboarding step is still pending —
  // including the logo/brand step, so it doesn't vanish the moment the last
  // "blocking" step (cert / CFE / fiscal profile / auto-flow) is marked done.
  // The accountant nudge stays a piggyback (it auto-resolves via hasAccountant
  // and is optional), so it alone doesn't keep the card open.
  if (
    !autoFlow &&
    !certPending &&
    !cfeEmitidosPending &&
    !cfeRecibidasPending &&
    !fiscalProfilePending &&
    !brandPending &&
    !sucursalPending &&
    !caePending
  )
    return null;

  const isAutoFailed = Boolean(autoFlow?.firstFailed);
  // The hero shows "X / Y" — completed / total. Only count items that
  // actually apply to this tenant, otherwise the denominator overstates the
  // work (e.g. showing /10 when the tenant only sees 4 actionable items).
  const certDone = Boolean(tenant) && !certPending;
  const cfeEmitidosDone = Boolean(tenant) && !cfeEmitidosPending;
  const cfeRecibidasDone = Boolean(tenant) && !cfeRecibidasPending;
  const brandDone = Boolean(tenant) && !brandPending;
  const accountantDone = Boolean(tenant) && !accountantPending;
  const fiscalProfileDone = Boolean(tenant) && !fiscalProfilePending;
  const sucursalDone = Boolean(tenant) && !sucursalPending;
  const tenantSteps =
    tenant?.onboardingSituationChoice === "scratch" ? SCRATCH_STEPS : STEPS;

  const progressRecord = isStringRecord(tenant?.dgiAutoOnboardingProgress)
    ? (tenant!.dgiAutoOnboardingProgress as Record<string, unknown>)
    : null;
  // The DGI auto-flow steps only apply to self-onboarded tenants (the ones
  // that ran the portal-user automation). Accountant/root-managed tenants
  // never start that chain, so its steps must not inflate the hero total.
  const autoFlowApplies = Boolean(progressRecord?.portal_user);
  const autoDoneCount = autoFlowApplies
    ? tenantSteps.filter((s) => Boolean(progressRecord?.[s.key])).length
    : 0;

  // Account items each apply only when relevant: cert / sucursal are skipped
  // for DGI-bypass tenants, fiscal profile is a paid-plan feature, the
  // accountant invite is a banner (not a counted step) in delegated mode;
  // brand / CFE always apply. These flags drive BOTH the X/Y count and which
  // rows render — every applicable step shows (done ones included), so the
  // visible list always matches the denominator.
  const brandApplicable = Boolean(tenant);
  const certApplicable = Boolean(tenant) && !tenant?.bypassDgi;
  const sucursalApplicable = Boolean(tenant) && !tenant?.bypassDgi;
  const cfeEmitidosApplicable = Boolean(tenant);
  const cfeRecibidasApplicable = Boolean(tenant);
  const accountantApplicable = Boolean(tenant) && !delegated;
  const fiscalProfileApplicable = Boolean(tenant) && tenant?.planId !== "free";
  // CAE upload is invisible on the dashboard for tenants that didn't run the
  // self DGI auto-flow (homologación / regularización / already_issuer) — they
  // don't get the auto-flow `cae_download` row. Surface it as an owner-doable
  // account step. Completion is an explicit "Marcar como hecho" (they add as
  // many CAE types as they use, then mark it done) — not auto after the first,
  // which would hide the row before they finished adding the rest.
  const { applicable: caeApplicable, done: caeDone } = computeCaeAccountStep({
    bypassDgi: Boolean(tenant?.bypassDgi),
    autoFlowApplies,
    dismissedSelf: dismissedAlertIds.includes(ONBOARDING_NUDGE_CAE),
    dismissedAccountant: dismissedAlertIds.includes(
      ONBOARDING_NUDGE_ACCT_CAE_LOADED,
    ),
  });

  const accountItems: ReadonlyArray<{ applicable: boolean; done: boolean }> = [
    { applicable: brandApplicable, done: brandDone },
    { applicable: certApplicable, done: certDone },
    { applicable: caeApplicable, done: caeDone },
    { applicable: sucursalApplicable, done: sucursalDone },
    { applicable: cfeEmitidosApplicable, done: cfeEmitidosDone },
    { applicable: cfeRecibidasApplicable, done: cfeRecibidasDone },
    { applicable: accountantApplicable, done: accountantDone },
    { applicable: fiscalProfileApplicable, done: fiscalProfileDone },
  ];
  const accountApplicable = accountItems.filter((i) => i.applicable).length;
  const accountDone = accountItems.filter((i) => i.applicable && i.done).length;

  const TOTAL_ITEMS =
    accountApplicable + (autoFlowApplies ? tenantSteps.length : 0);
  const doneCount = accountDone + autoDoneCount;
  const pendingItemsCount = TOTAL_ITEMS - doneCount;

  const monthOnlyFmt = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
  });
  const monthName = monthOnlyFmt.format(new Date());
  const periodLabel = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${new Date().getFullYear()}`;

  const statusLead = isAutoFailed
    ? t("dashboard.onboardingCard.statusFailedLead", "Necesitamos")
    : pendingItemsCount > 1
      ? t("dashboard.onboardingCard.statusManyLead", "Casi")
      : t("dashboard.onboardingCard.statusOneLead", "Un paso");
  const statusAccent = isAutoFailed
    ? t("dashboard.onboardingCard.statusFailedAccent", "tu atención.")
    : pendingItemsCount > 1
      ? t("dashboard.onboardingCard.statusManyAccent", "listo.")
      : t("dashboard.onboardingCard.statusOneAccent", "más.");

  const description = isAutoFailed
    ? t(
        "dashboard.onboardingCard.descFailed",
        "Nuestro equipo fue notificado y lo está revisando. Podés reintentar vos también.",
      )
    : autoFlow &&
        !certPending &&
        !sucursalPending &&
        !cfeEmitidosPending &&
        !cfeRecibidasPending &&
        !brandPending &&
        !accountantPending &&
        !fiscalProfilePending
      ? t(
          "dashboard.onboardingCard.descAutoOnly",
          "Fiscalo termina los pasos pendientes con DGI automáticamente — esta vista se actualiza sola.",
        )
      : t(
          "dashboard.onboardingCard.descActions",
          "Completá los pasos abajo para empezar a emitir comprobantes.",
        );

  // Show the account section whenever any account step applies — done steps
  // render too (with a done state) so the list matches the X/Y count.
  const showAccountSection = accountApplicable > 0;
  const doneSub = t(
    "dashboard.onboardingCard.accountStepDoneSub",
    "Listo · ya está configurado.",
  );

  return (
    <section className="space-y-3">
      <div className="ef-hero">
        <div>
          <div className="eyebrow">
            {t("dashboard.onboardingCard.eyebrow", "Onboarding")} · {periodLabel}
          </div>
          <div className="score">
            <span className="v">{doneCount}</span>
            <span className="max">/ {TOTAL_ITEMS}</span>
          </div>
          <div className="status">
            {statusLead} <span className="serif">{statusAccent}</span>
          </div>
          <div className="desc">
            {description}
            {isAutoFailed ? (
              <>
                {" "}
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    if (!retryMutation.isPending) retryMutation.mutate();
                  }}
                >
                  {retryMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {t("dashboard.onboardingCard.retry", "Reintentar")}
                </a>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {delegated && <DashboardAccountantHandoffBanner />}

      <div className="space-y-3">
        {showAccountSection && (
          <SectionPanel
            title={t(
              "dashboard.onboardingCard.accountSectionTitle",
              "Tu cuenta",
            )}
            hint={t(
              "dashboard.onboardingCard.accountSectionHint",
              "Pasos que dependen de vos — los hacés cuando quieras.",
            )}
          >
            {brandApplicable && (
              <StepRow
                state={brandDone ? "done" : "pending"}
                title={t(
                  "dashboard.onboardingCard.brandActionTitle",
                  "Personalizá tus facturas",
                )}
                subtitle={
                  brandDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.brandActionSub",
                        "Subí el logo y elegí el color que verán tus clientes en las facturas.",
                      )
                }
                icon={<Palette className="h-4 w-4" aria-hidden />}
              >
                {!brandDone && (
                  <>
                    <button
                      type="button"
                      className="fx-btn fx-btn-ghost"
                      onClick={dismissBrandNudge}
                    >
                      <Check className="mr-2 h-4 w-4" aria-hidden />
                      {t(
                        "dashboard.onboardingCard.brandMarkDone",
                        "Marcar como hecho",
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBrandModalOpen(true)}
                      className="fx-btn fx-btn-accent shrink-0"
                    >
                      {t(
                        "dashboard.onboardingCard.brandActionCta",
                        "Subí tu logo",
                      )}
                      <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                    </button>
                  </>
                )}
              </StepRow>
            )}

            {sucursalApplicable && (
              <StepRow
                state={sucursalDone ? "done" : "pending"}
                title={t(
                  "dashboard.onboardingCard.sucursalActionTitle",
                  "Confirmá tu sucursal DGI",
                )}
                subtitle={
                  sucursalDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.sucursalActionSub",
                        "No pudimos detectar tu código de sucursal (CodSuc) en DGI. Confirmalo para que el reporte diario no sea rechazado.",
                      )
                }
                icon={<Store className="h-4 w-4" aria-hidden />}
              >
                {sucursalDone ? null : delegated ? (
                  <ContadorTag t={t} />
                ) : (
                  <button
                    type="button"
                    onClick={() => setSucursalModalOpen(true)}
                    className="fx-btn fx-btn-accent shrink-0"
                  >
                    {t(
                      "dashboard.onboardingCard.sucursalActionCta",
                      "Confirmar sucursal",
                    )}
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                  </button>
                )}
              </StepRow>
            )}

            {fiscalProfileApplicable && (
              <StepRow
                state={fiscalProfileDone ? "done" : "pending"}
                title={t(
                  "dashboard.onboardingCard.fiscalProfileTitle",
                  "Configurá el perfil fiscal",
                )}
                subtitle={
                  fiscalProfileDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.fiscalProfileSub",
                        "Decinos qué impuestos paga la empresa (IVA, IRAE, BPS, IRPF, IRNR). Con eso armamos la agenda de vencimientos.",
                      )
                }
                icon={<Landmark className="h-4 w-4" aria-hidden />}
              >
                {fiscalProfileDone ? null : delegated ? (
                  <ContadorTag t={t} />
                ) : (
                  <button
                    type="button"
                    className="fx-btn fx-btn-accent shrink-0"
                    onClick={() => navigate("/settings?tab=fiscal-legal")}
                  >
                    {t(
                      "dashboard.onboardingCard.fiscalProfileCta",
                      "Configurar perfil",
                    )}
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                  </button>
                )}
              </StepRow>
            )}

            {certApplicable && (
              <StepRow
                state={certDone ? "done" : "pending"}
                title={t("settings.certificate.title")}
                subtitle={
                  certDone ? doneSub : t("settings.certificate.description")
                }
                icon={<ShieldCheck className="h-4 w-4" aria-hidden />}
              >
                {certDone ? null : delegated ? (
                  <ContadorTag t={t} />
                ) : (
                  <button
                    type="button"
                    onClick={() => setCertModalOpen(true)}
                    className="fx-btn fx-btn-accent shrink-0"
                  >
                    {t("settings.certificate.uploadCertificate")}
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                  </button>
                )}
              </StepRow>
            )}

            {caeApplicable && (
              <StepRow
                state={caeDone ? "done" : "pending"}
                title={t("dashboard.onboardingCard.caeTitle", "Cargá tus CAE")}
                subtitle={
                  caeDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.caeSub",
                        "Subí las Constancias de Autorización de Emisión (CAE) para poder facturar.",
                      )
                }
                icon={<FileCheck className="h-4 w-4" aria-hidden />}
              >
                {!caeDone && (
                  <>
                    <button
                      type="button"
                      className="fx-btn fx-btn-ghost"
                      onClick={dismissCaeNudge}
                    >
                      <Check className="mr-2 h-4 w-4" aria-hidden />
                      {t(
                        "dashboard.onboardingCard.caeMarkDone",
                        "Marcar como hecho",
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCaeModalOpen(true)}
                      className="fx-btn fx-btn-accent shrink-0"
                    >
                      {t("dashboard.onboardingCard.caeCta", "Cargar CAE")}
                    </button>
                  </>
                )}
              </StepRow>
            )}

            {cfeEmitidosApplicable && (
              <StepRow
                state={cfeEmitidosDone ? "done" : "pending"}
                title={t(
                  "dashboard.onboardingCard.cfeEmitidosTitle",
                  "CFE emitidos",
                )}
                subtitle={
                  cfeEmitidosDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.cfeEmitidosSub",
                        "Importá los comprobantes que tu empresa ya emitió (XML o ZIP).",
                      )
                }
                icon={<FileText className="h-4 w-4" aria-hidden />}
              >
                {!cfeEmitidosDone && (
                  <>
                    <button
                      type="button"
                      className="fx-btn fx-btn-ghost"
                      onClick={dismissCfeEmitidosNudge}
                    >
                      <Check className="mr-2 h-4 w-4" aria-hidden />
                      {t(
                        "dashboard.onboardingCard.cfeMarkDone",
                        "Marcar como hecho",
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCfeModalOpen(true)}
                      className="fx-btn fx-btn-accent shrink-0"
                    >
                      {t("settings.cfe.importEmitted")}
                    </button>
                  </>
                )}
              </StepRow>
            )}

            {cfeRecibidasApplicable && (
              <StepRow
                state={cfeRecibidasDone ? "done" : "pending"}
                title={t(
                  "dashboard.onboardingCard.cfeRecibidasTitle",
                  "CFE recibidos",
                )}
                subtitle={
                  cfeRecibidasDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.cfeRecibidasSub",
                        "Importá los comprobantes que recibís de tus proveedores (XML o ZIP).",
                      )
                }
                icon={<FileText className="h-4 w-4" aria-hidden />}
              >
                {!cfeRecibidasDone && (
                  <>
                    <button
                      type="button"
                      className="fx-btn fx-btn-ghost"
                      onClick={dismissCfeRecibidasNudge}
                    >
                      <Check className="mr-2 h-4 w-4" aria-hidden />
                      {t(
                        "dashboard.onboardingCard.cfeMarkDone",
                        "Marcar como hecho",
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRecibidasModalOpen(true)}
                      className="fx-btn fx-btn-accent shrink-0"
                    >
                      {t(
                        "settings.cfe.importRecibidas",
                        "Importar CFE recibidos",
                      )}
                    </button>
                  </>
                )}
              </StepRow>
            )}

            {accountantApplicable && (
              <StepRow
                state={accountantDone ? "done" : "pending"}
                title={t(
                  "dashboard.onboardingCard.accountantActionTitle",
                  "Invitá a tu contador",
                )}
                subtitle={
                  accountantDone
                    ? doneSub
                    : t(
                        "dashboard.onboardingCard.accountantActionSub",
                        "Compartí acceso de solo-lectura para que vea cada movimiento sin tener que pedirte nada.",
                      )
                }
                icon={<UserPlus className="h-4 w-4" aria-hidden />}
              >
                {!accountantDone && (
                  <>
                    <button
                      type="button"
                      className="fx-btn fx-btn-ghost"
                      onClick={dismissAccountantNudge}
                    >
                      <Check className="mr-2 h-4 w-4" aria-hidden />
                      {t(
                        "dashboard.onboardingCard.accountantMarkDone",
                        "Marcar como hecho",
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccountantModalOpen(true)}
                      className="fx-btn fx-btn-accent shrink-0"
                    >
                      {t(
                        "dashboard.onboardingCard.accountantActionCta",
                        "Invitar contador",
                      )}
                      <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                    </button>
                  </>
                )}
              </StepRow>
            )}
          </SectionPanel>
        )}

        {autoFlow && (
          <SectionPanel
            title={t(
              "dashboard.onboardingCard.dgiSectionTitle",
              "Configuración con DGI",
            )}
            hint={
              delegated
                ? t(
                    "dashboard.onboardingCard.dgiSectionHintDelegated",
                    "Lo hace tu contador junto con Fiscalo — vos no tenés que tocar nada.",
                  )
                : t(
                    "dashboard.onboardingCard.dgiSectionHint",
                    "Lo hace Fiscalo en segundo plano — vos no tenés que tocar nada.",
                  )
            }
          >
            {tenantSteps.map((s) => {
              const done = Boolean(autoFlow.progress[s.key]);
              const stepFailed = Boolean(autoFlow.failed[s.key]);
              const inProgress =
                !done &&
                !stepFailed &&
                autoFlow.firstPending?.key === s.key &&
                !isAutoFailed;
              const state: StepRowState = done
                ? "done"
                : stepFailed
                  ? "failed"
                  : inProgress
                    ? "running"
                    : "pending";
              return (
                <StepRow
                  key={s.key}
                  state={state}
                  title={t(s.labelKey, s.fallback)}
                  subtitle={
                    state === "done"
                      ? t(
                          "dashboard.onboardingCard.autoStepDone",
                          "Listo · Fiscalo lo configuró por vos.",
                        )
                      : state === "running"
                        ? t(
                            "dashboard.onboardingCard.autoStepRunning",
                            "En proceso · seguimos en segundo plano.",
                          )
                        : state === "failed"
                          ? autoFlow.failed[s.key] ??
                            t(
                              "dashboard.onboardingCard.autoStepFailed",
                              "Hubo un problema · nuestro equipo está revisando.",
                            )
                          : t(
                              "dashboard.onboardingCard.autoStepPending",
                              "Pendiente · arranca apenas termine el paso anterior.",
                            )
                  }
                  muted={state === "pending"}
                />
              );
            })}
          </SectionPanel>
        )}
      </div>

      <OnboardingBrandModal
        open={brandModalOpen}
        onOpenChange={setBrandModalOpen}
        currentLogoUrl={tenant?.invoiceLogoImage ?? tenant?.profileImage ?? null}
        currentBrandColor={tenant?.brandColor ?? null}
      />
      <OnboardingSucursalModal
        open={sucursalModalOpen}
        onOpenChange={setSucursalModalOpen}
        currentSucursal={tenant?.cdgDGISucur ?? null}
      />
      <OnboardingCertModal
        open={certModalOpen}
        onOpenChange={setCertModalOpen}
        tenantRut={tenant?.rut ?? null}
      />
      <AddCaeDialog
        open={caeModalOpen}
        onOpenChange={setCaeModalOpen}
        tenantId={tenant?.id ?? ""}
      />
      <OnboardingCfeModal
        open={cfeModalOpen}
        onOpenChange={setCfeModalOpen}
        tenantId={tenant?.id ?? ""}
        tenantName={tenant?.name ?? ""}
      />
      <ImportRecibidasCfeDialog
        open={recibidasModalOpen}
        onOpenChange={setRecibidasModalOpen}
        tenantId={tenant?.id ?? ""}
        tenantName={tenant?.name ?? ""}
        importFn={importRecibidasCfes}
      />
      {tenant?.id ? (
        <OnboardingInviteAccountantModal
          open={accountantModalOpen}
          onOpenChange={setAccountantModalOpen}
          tenantId={tenant.id}
          onInvited={dismissAccountantNudge}
        />
      ) : null}
    </section>
  );
}

interface SectionPanelProps {
  title: string;
  hint?: string;
  children: React.ReactNode;
}

function SectionPanel({ title, hint, children }: SectionPanelProps): JSX.Element {
  return (
    <div className="rounded-[var(--fx-r-md)] border border-[var(--fx-ink-100)] dark:border-[var(--dk-border)] bg-white dark:bg-[var(--dk-surface)]">
      <div className="border-b border-[var(--fx-ink-100)] dark:border-[var(--dk-border)] px-5 py-3">
        <div className="font-[family:var(--fx-ff-mono)] text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]">
          {title}
        </div>
        {hint ? (
          <div className="mt-1 text-xs text-[var(--fx-ink-400)] dark:text-[var(--dk-text-mute)]">{hint}</div>
        ) : null}
      </div>
      <ol className="divide-y divide-[var(--fx-ink-100)] dark:divide-[var(--dk-border)]">{children}</ol>
    </div>
  );
}

type StepRowState = "pending" | "running" | "done" | "failed";

interface StepRowProps {
  state: StepRowState;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  icon?: JSX.Element;
  children?: React.ReactNode;
  muted?: boolean;
}

function StepRow({
  state,
  title,
  subtitle,
  icon,
  children,
  muted,
}: StepRowProps): JSX.Element {
  const statusIcon =
    state === "done" ? (
      <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
    ) : state === "running" ? (
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
    ) : state === "failed" ? (
      <AlertTriangle className="h-3 w-3" aria-hidden />
    ) : null;

  const tone =
    state === "done"
      ? "bg-[var(--fx-accent-500)] text-white"
      : state === "running"
        ? "bg-[rgba(45,97,73,0.10)] dark:bg-[rgba(111,165,133,0.16)] text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]"
        : state === "failed"
          ? "bg-[rgba(217,119,6,0.12)] dark:bg-[rgba(232,196,144,0.14)] text-[#92400e] dark:text-[#E8C490]"
          : "bg-transparent border border-[var(--fx-ink-200)] dark:border-[var(--dk-border-2)] text-[var(--fx-ink-400)] dark:text-[var(--dk-text-mute)]";

  return (
    <li
      className={`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center ${
        muted ? "opacity-90" : ""
      }`}
    >
      <span
        aria-hidden
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone}`}
      >
        {icon && state === "pending" && !statusIcon ? icon : statusIcon}
      </span>
      <div className="flex-1 min-w-0">
        <div
          className={`text-sm font-semibold ${
            state === "failed"
              ? "text-[#92400e] dark:text-[#E8C490]"
              : state === "done"
                ? "text-[var(--fx-ink-700)] dark:text-[var(--dk-text-soft)]"
                : "text-[var(--fx-ink-900)] dark:text-[var(--dk-text)]"
          }`}
        >
          {title}
        </div>
        <div className="mt-0.5 text-xs text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]">
          {subtitle}
        </div>
      </div>
      {children ? (
        <div className="flex shrink-0 items-center gap-2">{children}</div>
      ) : null}
    </li>
  );
}

/** Read-only badge shown on a delegated owner's technical steps — signals the
 *  step is the accountant's job, so the owner sees it without a call-to-action. */
function ContadorTag({
  t,
}: {
  t: (key: string, fallback: string) => string;
}): JSX.Element {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--fx-ink-200)] dark:border-[var(--dk-border-2)] px-3 py-1 text-xs font-medium text-[var(--fx-ink-500)] dark:text-[var(--dk-text-mute)]">
      <UserCog className="h-3.5 w-3.5" aria-hidden />
      {t("dashboard.onboardingCard.delegatedStepTag", "Lo hace tu contador")}
    </span>
  );
}
